/**
 * FEEDBACK WIDGET — USER ACTIONS TRACKER (TICKET-CRM-14)
 * Trace les interactions récentes (clicks, navigation, inputs) pour les
 * étapes de reproduction du ticket. Port de benevoles ; la navigation
 * react-router est poussée manuellement via trackNavigation() (pas de
 * popstate sur les pushState du router).
 */

import type { UserAction } from './feedback.types';

const MAX_ACTIONS = 20;
const INPUT_DEBOUNCE_MS = 500;

class UserActionsTracker {
  private actions: UserAction[] = [];
  private isInitialized = false;
  private inputDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private boundHandleClick: (e: MouseEvent) => void;
  private boundHandleInput: (e: Event) => void;

  constructor() {
    this.boundHandleClick = this.handleClick.bind(this);
    this.boundHandleInput = this.handleInput.bind(this);
  }

  initialize(): void {
    if (this.isInitialized || typeof window === 'undefined') {
      return;
    }
    document.addEventListener('click', this.boundHandleClick, { capture: true });
    document.addEventListener('input', this.boundHandleInput, { capture: true });
    this.isInitialized = true;
  }

  destroy(): void {
    if (!this.isInitialized || typeof window === 'undefined') {
      return;
    }
    document.removeEventListener('click', this.boundHandleClick, { capture: true });
    document.removeEventListener('input', this.boundHandleInput, { capture: true });
    if (this.inputDebounceTimer) {
      clearTimeout(this.inputDebounceTimer);
    }
    this.isInitialized = false;
  }

  private handleClick(e: MouseEvent): void {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    this.addAction({
      type: 'click',
      timestamp: new Date().toISOString(),
      target: this.getElementDescription(target),
      details: target.textContent?.slice(0, 50) || undefined,
    });
  }

  private handleInput(e: Event): void {
    if (this.inputDebounceTimer) {
      clearTimeout(this.inputDebounceTimer);
    }

    this.inputDebounceTimer = setTimeout(() => {
      const target = e.target as HTMLInputElement | null;
      if (!target) return;

      const details = this.isSensitiveInput(target)
        ? '[REDACTED]'
        : target.value?.slice(0, 30) || undefined;

      this.addAction({
        type: 'input',
        timestamp: new Date().toISOString(),
        target: this.getElementDescription(target),
        details,
      });
    }, INPUT_DEBOUNCE_MS);
  }

  private isSensitiveInput(element: HTMLInputElement): boolean {
    // Élargi vs benevoles : email/phone/iban = données perso CRM.
    const sensitiveTypes = ['password', 'email', 'tel'];
    const sensitiveNames = ['password', 'secret', 'token', 'key', 'iban', 'email', 'phone'];

    if (sensitiveTypes.includes(element.type)) return true;

    const name = (element.name || '').toLowerCase();
    const id = (element.id || '').toLowerCase();
    return sensitiveNames.some((s) => name.includes(s) || id.includes(s));
  }

  private getElementDescription(element: HTMLElement): string {
    const parts: string[] = [element.tagName.toLowerCase()];

    if (element.id) {
      parts.push(`#${element.id}`);
    }
    if (element.className && typeof element.className === 'string') {
      const classes = element.className.split(' ').filter(Boolean).slice(0, 2);
      if (classes.length > 0) {
        parts.push(`.${classes.join('.')}`);
      }
    }
    if (element instanceof HTMLInputElement && element.type) {
      parts.push(`[type="${element.type}"]`);
    }

    const ariaLabel = element.getAttribute('aria-label');
    const placeholder = element.getAttribute('placeholder');
    if (ariaLabel) {
      parts.push(`"${ariaLabel.slice(0, 20)}"`);
    } else if (placeholder) {
      parts.push(`"${placeholder.slice(0, 20)}"`);
    }

    return parts.join('');
  }

  trackNavigation(pathname: string): void {
    this.addAction({
      type: 'navigation',
      timestamp: new Date().toISOString(),
      details: pathname,
    });
  }

  private addAction(action: UserAction): void {
    this.actions.push(action);
    if (this.actions.length > MAX_ACTIONS) {
      this.actions.shift();
    }
  }

  getActions(): UserAction[] {
    return [...this.actions];
  }

  clear(): void {
    this.actions = [];
  }

  isActive(): boolean {
    return this.isInitialized;
  }
}

export const userActionsTracker = new UserActionsTracker();
